import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { playVictorySound, playRetrySound } from '../utils/sounds';

interface ReviewResultsProps {
  passed: boolean;
  isLastLevel: boolean;
  onNextLevel: () => void;
  onRetry: () => void;
}

const ReviewResults: React.FC<ReviewResultsProps> = ({
  passed,
  isLastLevel,
  onNextLevel,
  onRetry,
}) => {
  useEffect(() => {
    if (!passed) {
      playRetrySound();
      return;
    }

    playVictorySound();

    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    // Second burst slightly delayed for a fuller effect
    const timer = setTimeout(() => {
      confetti({
        particleCount: 60,
        spread: 100,
        origin: { y: 0.5 },
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [passed]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full"
    >
      {passed ? (
        <>
          <motion.div
            className="text-6xl mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.3, 1] }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            &#11088;
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Great Job!</h2>
          <p className="text-gray-500 mb-6">
            {isLastLevel
              ? 'You\'ve completed all 11 levels!'
              : 'You\'re ready for the next level!'}
          </p>
          <button
            onClick={onNextLevel}
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-lg"
          >
            {isLastLevel ? 'Finish Module' : 'Next Level'}
          </button>
        </>
      ) : (
        <>
          <div className="text-6xl mb-4">&#127775;</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Almost there!</h2>
          <p className="text-gray-500 mb-6">Let's try one more time!</p>
          <button
            onClick={onRetry}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-lg"
          >
            Let's Go!
          </button>
        </>
      )}
    </motion.div>
  );
};

export default ReviewResults;
